import { FC, useMemo } from 'react';

import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import { useFormContext } from 'react-hook-form-mui';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import IconImg from 'src/assets/images/icons/receipt-landing.svg';

import { ServiceProviderDetailsDto } from 'src/generated/services/TFinancialApi';

import { LandingSection } from './Section';
import { MultipleSelectDropdown } from '../analytics/filter/MultipleSelectDropdown';
import { IAuditProps, IServiceList } from './types';
import { LandingAuditProviders } from './AuditProviders';
import { useTourContext } from 'src/presentations/guided-tours';

const StyledBox = styled(Box)(({ theme }) => ({
  '& .MuiFormControl-root': {
    width: '180px',
  },
  '& .MuiInputBase-root': {
    borderRadius: '8px',
  },
  '& .MuiInputBase-input': {
    fontSize: '16px',
    padding: '8px 16px',
  },
  [theme.breakpoints.down(1520)]: {
    '& .MuiInputBase-input': {
      fontSize: '14px',
      padding: '6px 12px',
    },
  },
}));

const sortOptions = [
  {
    label: 'Action Needed',
    value: 'ACTION_NEEDED',
  },
  {
    label: 'Date (Newest to Oldest)',
    value: 'DATE_DESC',
  },
  {
    label: 'Date (Oldest to Newest)',
    value: 'DATE_ASC',
  },
];

const getActionNeededTotals = (item: ServiceProviderDetailsDto) => {
  let actionTotal = 0;
  item?.invoiceStatuses?.forEach((status) => {
    actionTotal += status?.count || 0;
  });

  return actionTotal;
};

export const LandingAudit: FC<IAuditProps> = ({ providers, isLoading, onRefresh }) => {
  const { watch, control } = useFormContext();
  const sort = watch('auditSort');

  const listProviders = useMemo(() => {
    const groupedItems: IServiceList = {
      AIR: [],
      ROAD: [],
      SEA: [],
      CT: [],
      COURIER: [],
    };
    if (providers) {
      const newItems = [...providers];
      newItems.sort((a, b) => {
        let compareStatus = 0;

        if (sort === 'DATE_DESC' || sort === 'DATE_ASC') {
          const aDate = new Date(a.latestInvoiceCreatedDate);
          const bDate = new Date(b.latestInvoiceCreatedDate);
          const compareDateStatus = sort === 'DATE_DESC' ? isBefore(aDate, bDate) : isAfter(aDate, bDate);
          compareStatus = compareDateStatus ? 1 : -1;
        } else if (sort === 'SERVICE_PROVIDER') {
          const aProvider = a.serviceProviderCode;
          const bProvider = b.serviceProviderCode;
          compareStatus = `${aProvider}`.localeCompare(bProvider);
        } else if (sort === 'MODALITY') {
          const aModality = a.modality;
          const bModality = b.modality;
          compareStatus = `${aModality}`.localeCompare(bModality);
        } else {
          const aTotal = getActionNeededTotals(a);
          const bTotal = getActionNeededTotals(b);
          compareStatus = bTotal - aTotal;
        }

        return compareStatus;
      });
      newItems.forEach((item) => {
        const modality = item?.modality;

        if (typeof groupedItems[modality] !== 'undefined') {
          groupedItems[modality].push(item);
        }
      });

      return groupedItems;
    }
    return [];
  }, [providers, sort]);

  const tour = useTourContext().appTour.registerTour('auditLanding');
  const categoryIdGen = (title: string) => tour.registerStep('category', [], true, (id) => `${id}-${title}`);
  const cardIdGen = (title: string) => tour.registerStep('card', [], true, (id) => `${id}-${title}`);

  return (
    <LandingSection
      id={tour.registerStep('title')}
      icon={IconImg}
      title='Audit Exceptions'
      linkDetail='/'
      isLoading={isLoading}
      onRefresh={onRefresh}
      topRight={
        <div className='flex items-center gap-24'>
          <StyledBox id={tour.registerStep('sortDropdown')}>
            <MultipleSelectDropdown name='auditSort' label='Sort by' control={control} options={sortOptions} multiple={false} />
          </StyledBox>
        </div>
      }
    >
      <div className='flex flex-col gap-10 xl:gap-24'>
        {['AIR', 'CT', 'ROAD', 'SEA', 'COURIER'].map((modality) => {
          if (listProviders[modality] && listProviders[modality]?.length > 0) {
            return (
              <LandingAuditProviders
                key={`providers-${modality}`}
                modality={modality as ServiceProviderDetailsDto['modality']}
                providers={listProviders[modality]}
                categoryIdGen={categoryIdGen}
                cardIdGen={cardIdGen}
              />
            );
          }
          return null;
        })}
      </div>
    </LandingSection>
  );
};
